import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { type CredentialResponse } from '@react-oauth/google';
import { GoogleSignInBlock } from '@/components/auth/GoogleSignInBlock';
import { ThemeToggleButton } from '@/components/theme/ThemeToggleButton';
import { TifosiCrest } from '@/components/ui/TifosiCrest';
import { Colors } from '@/constants/Colors';
import CONFIG from '@/constants/Config';
import { useTheme } from '@/hooks/useTheme';
import { useProfileStore } from '@/stores/profileStore';
import { captureTrafficSource } from '@/utils/trafficSource';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage() {
  const colors = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { googleId, loadProfile, signInWithGoogle } = useProfileStore();
  const [authError, setAuthError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => { void loadProfile(); }, [loadProfile]);
  useEffect(() => { captureTrafficSource(); }, []);

  const redirectTarget = (location.state as LocationState | null)?.from?.pathname || '/';

  useEffect(() => {
    if (googleId) {
      navigate(redirectTarget, { replace: true });
    }
  }, [googleId, navigate, redirectTarget]);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    setAuthError(null);
    if (!response.credential) {
      setAuthError('Connexion Google interrompue.');
      return;
    }
    setIsPending(true);
    try {
      await signInWithGoogle(response.credential);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Connexion Google impossible.');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        height: '100dvh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        gap: 16,
        backgroundColor: colors.background,
        paddingTop: 'calc(20px + env(safe-area-inset-top))',
        paddingRight: 'calc(24px + env(safe-area-inset-right))',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
        paddingLeft: 'calc(24px + env(safe-area-inset-left))',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 'calc(12px + env(safe-area-inset-top))',
          right: 'calc(12px + env(safe-area-inset-right))',
          zIndex: 2,
        }}
      >
        <ThemeToggleButton />
      </div>
      <div className="login-hero-stagger" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <TifosiCrest size={96} text="T" fontSize={56} />
        </div>
        <span
          className="tifosi-wordmark"
          style={{ fontSize: 36, color: colors.text }}
        >
          TIFOSI
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.4, color: Colors.primary, textAlign: 'center' }}>
          LE SCOUTING COMMENCE DANS LES TRIBUNES
        </span>
        <span style={{ fontSize: 12, color: colors.textSecondary, textAlign: 'center', maxWidth: 340, lineHeight: 1.45 }}>
          L'application mobile d'observation du football amateur.
          <br />
          Donnez votre avis en 30 secondes et révélez les talents locaux.
        </span>
      </div>

      <div className="login-card-shell">
        <div className="login-card-ring-spin" aria-hidden />
        <div
          className="login-card-anim"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
            width: '100%',
            padding: 18,
            borderRadius: 24,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 8px 24px ${Colors.primary}12`,
          }}
        >
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: colors.textSecondary }}>
          CONNEXION REQUISE
        </span>
        <span style={{ fontSize: 16, fontWeight: 600, color: colors.text, textAlign: 'center' }}>
          Connectez-vous et faites vous entendre.
        </span>
        <div style={{ display: 'grid', gap: 8, width: '100%', maxWidth: 320 }}>
          <span style={{ fontSize: 10, color: colors.textSecondary, textAlign: 'center' }}>
            Trouvez les matchs autour de vous
          </span>
          <span style={{ fontSize: 10, color: colors.textSecondary, textAlign: 'center' }}>
            Notez les joueurs en live depuis le stade
          </span>
        </div>

        <GoogleSignInBlock
          clientConfigured={Boolean(GOOGLE_CLIENT_ID)}
          pending={isPending}
          error={authError}
          onSuccess={handleGoogleSuccess}
          onError={() => setAuthError('Connexion Google échouée.')}
          text="continue_with"
        />

        <span style={{ fontSize: 8, color: colors.textSecondary, textAlign: 'center', maxWidth: 280, marginTop: 8 }}>
          Vos informations Google (nom, email, photo) sont utilisees uniquement pour identifier vos observations et securiser la
          fiabilite des notes.
        </span>
        </div>
      </div>

      <div className="login-footer-anim" style={{ textAlign: 'center', fontSize: 11, color: colors.textSecondary, marginTop: 'auto' }}>
        Tifosi - Le foot amateur vu depuis les tribunes
        <br />
        Version {CONFIG.APP_VERSION}
      </div>
    </div>
  );
}
